import { supabase } from './supabaseClient';
import { CreditAllocation, CustomerCredit, Invoice, InvoicePayment } from '../types';
import {
    allocatePayment,
    hasBlockingErrors,
    openAmountCents,
    overpaymentCents,
    PaymentDraft,
    validatePayment,
    ValidationMessage,
} from './invoiceLogic';
import { assertCents, formatCents } from './money';
import { checkRemainder, isRemainderMethod, REMAINDER_LABEL, RemainderKind } from './paymentRemainder';
import { baseBankTxId, splitBankTxId } from './bank/bankTxId';

export { baseBankTxId, splitBankTxId } from './bank/bankTxId';

/**
 * Zahlungen, Kundenguthaben und Mahnstufen.
 *
 * Eigene Datei statt weiterer Zeilen in invoiceService: das Ausstellen ist ein
 * einmaliger, unumkehrbarer Vorgang, die Zahlungserfassung ein laufender.
 *
 * Wichtig fuer das Verstaendnis dieses Moduls: paid_cents, credited_cents und
 * der Zahlungsstatus der Rechnung werden hier NIE geschrieben. Das erledigt der
 * Datenbank-Trigger invoices_apply_settlement aus invoices_v3.sql, der die
 * Aggregate aus den Belegzeilen neu berechnet. Ein Read-Modify-Write aus dem
 * Browser wuerde bei zwei gleichzeitigen Buchungen eine davon verlieren.
 */

/**
 * Geldspalten sind BIGINT und kommen von PostgREST als String.
 *
 * assertCents statt Number(v ?? 0): ein stiller Ausfall auf 0 waere hier
 * schlimmer als ein Fehler. Ein Zahlungsbetrag, der als 0 durchrutscht, faellt
 * niemandem auf — er verschwindet nur aus der Summe.
 */
const cents = (v: unknown, context: string): number => assertCents(Number(v ?? 0), context);

// ─── Mapper ─────────────────────────────────────────────────────────────────

const mapPaymentRow = (row: any): InvoicePayment => ({
    id: row.id,
    invoiceId: row.invoice_id,
    direction: row.direction,
    valueDate: row.value_date,
    amountCents: cents(row.amount_cents, 'invoice_payments.amount_cents'),
    allocatedNetCents: cents(row.allocated_net_cents, 'invoice_payments.allocated_net_cents'),
    allocatedTaxCents: cents(row.allocated_tax_cents, 'invoice_payments.allocated_tax_cents'),
    method: row.method,
    reference: row.reference ?? undefined,
    bankTxId: row.bank_tx_id ?? undefined,
    note: row.note ?? undefined,
    createdBy: row.created_by,
    createdAt: row.created_at,
});

const mapCreditRow = (row: any): CustomerCredit => ({
    id: row.id,
    customerId: row.customer_id,
    sourceInvoiceId: row.source_invoice_id ?? undefined,
    sourcePaymentId: row.source_payment_id ?? undefined,
    amountCents: cents(row.amount_cents, 'customer_credits.amount_cents'),
    usedCents: cents(row.used_cents, 'customer_credits.used_cents'),
    reason: row.reason,
    note: row.note ?? undefined,
    createdAt: row.created_at,
    createdBy: row.created_by,
});

const mapAllocationRow = (row: any): CreditAllocation => ({
    id: row.id,
    creditId: row.credit_id,
    targetInvoiceId: row.target_invoice_id,
    allocatedCents: cents(row.allocated_cents, 'credit_allocations.allocated_cents'),
    allocatedAt: row.allocated_at,
    allocatedBy: row.allocated_by,
});

// ─── Lesen ──────────────────────────────────────────────────────────────────

export const fetchPayments = async (invoiceId: string): Promise<InvoicePayment[]> => {
    const { data, error } = await supabase
        .from('invoice_payments')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('value_date', { ascending: true })
        .order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []).map(mapPaymentRow);
};

/** Zahlungen zu mehreren Rechnungen auf einmal — fuer Listen und Kennzahlen. */
/**
 * Groesse eines Abfrageblocks.
 *
 * PostgREST setzt den IN-Filter in die URL. Bei einigen hundert Rechnungen
 * reisst die Laengengrenze — und das Ergebnis waere nicht etwa ein Fehler,
 * sondern ein abgeschnittener Filter, der ZU WENIGE Zahlungen findet. Bereits
 * gebuchte Umsaetze erschienen dann wieder als offen. Die Blockbildung gehoert
 * deshalb hierher und nicht in die Aufrufer: sonst hat sie einer und der
 * naechste nicht.
 */
const INVOICE_CHUNK = 100;

export const fetchPaymentsForInvoices = async (
    invoiceIds: string[],
): Promise<Map<string, InvoicePayment[]>> => {
    const out = new Map<string, InvoicePayment[]>();
    if (!invoiceIds.length) return out;

    for (let i = 0; i < invoiceIds.length; i += INVOICE_CHUNK) {
        const chunk = invoiceIds.slice(i, i + INVOICE_CHUNK);
        const { data, error } = await supabase
            .from('invoice_payments')
            .select('*')
            .in('invoice_id', chunk)
            .order('value_date', { ascending: true });
        if (error) throw error;
        for (const row of data || []) {
            const p = mapPaymentRow(row);
            const list = out.get(p.invoiceId);
            if (list) list.push(p);
            else out.set(p.invoiceId, [p]);
        }
    }
    return out;
};

export const fetchCustomerCredits = async (customerId: string): Promise<CustomerCredit[]> => {
    const { data, error } = await supabase
        .from('customer_credits')
        .select('*')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(mapCreditRow);
};

/** Verfuegbares Guthaben eines Kunden in Cent. */
export const fetchAvailableCreditCents = async (customerId: string): Promise<number> => {
    const { data, error } = await supabase
        .from('v_customer_credit_balance')
        .select('available_cents')
        .eq('customer_id', customerId)
        .maybeSingle();
    if (error) throw error;
    return cents(data?.available_cents, 'v_customer_credit_balance.available_cents');
};

export const fetchCreditAllocations = async (creditId: string): Promise<CreditAllocation[]> => {
    const { data, error } = await supabase
        .from('credit_allocations')
        .select('*')
        .eq('credit_id', creditId)
        .order('allocated_at', { ascending: true });
    if (error) throw error;
    return (data || []).map(mapAllocationRow);
};

// ─── Zahlung erfassen ───────────────────────────────────────────────────────

export interface AddPaymentInput extends PaymentDraft {
    invoiceId: string;
    /** Idempotenzschluessel aus dem Bankimport (M8). Manuell erfasst: leer. */
    bankTxId?: string;
    /**
     * Netto/Steuer-Aufteilung. Nur noetig, wenn die Rechnung mehrere
     * Steuersaetze traegt — dann kann sie nicht automatisch bestimmt werden.
     */
    allocation?: { allocatedNetCents: number; allocatedTaxCents: number };
}

export class PaymentValidationError extends Error {
    constructor(
        message: string,
        public readonly messages: ValidationMessage[],
    ) {
        super(message);
        this.name = 'PaymentValidationError';
    }
}

const validationError = (messages: ValidationMessage[], fallback: string): PaymentValidationError =>
    new PaymentValidationError(
        messages.find((m) => m.level === 'error')?.message ?? fallback,
        messages,
    );

/**
 * Prueft eine Zahlung und baut die Datenbankzeile — ohne zu schreiben.
 *
 * Getrennt von addPayment, damit Zahlung und Skonto dieselben Pruefungen
 * durchlaufen und trotzdem in EINER Anweisung geschrieben werden koennen
 * (addPaymentWithCashDiscount).
 */
const preparePaymentRow = (
    input: AddPaymentInput,
    invoice: Invoice,
    todayIso: string,
    alongsideCents: number = 0,
) => {
    const messages = validatePayment(input, invoice, todayIso);
    if (hasBlockingErrors(messages)) {
        throw validationError(messages, 'Die Zahlung ist ungültig.');
    }

    // Skonto und Sicherheitseinbehalt sind kein Geld: sie gleichen nur den
    // Rest aus. Sie gehen immer in Richtung Eingang, tragen keine
    // Bankreferenz (die gehoert der Ueberweisung) und duerfen den offenen
    // Betrag nicht uebersteigen — eine "Ueberzahlung" daraus wuerde zum
    // Kundenguthaben, das nie gezahlt wurde.
    if (isRemainderMethod(input.method)) {
        const label = REMAINDER_LABEL[input.method];
        if (input.direction !== 'INCOMING' || input.bankTxId) {
            throw new PaymentValidationError(
                `${label} wird nur als Eingang und ohne Kontoauszugsbezug gebucht.`,
                [],
            );
        }
        const remainderMessages = checkRemainder(
            invoice,
            input.method,
            input.amountCents,
            input.valueDate,
            alongsideCents,
        );
        if (hasBlockingErrors(remainderMessages)) {
            throw validationError(remainderMessages, `${label} ist ungültig.`);
        }
    }

    let allocation = input.allocation;
    if (!allocation) {
        const auto = allocatePayment(input.amountCents, invoice.totals.taxGroups);
        if (!auto) {
            throw new PaymentValidationError(
                'Die Rechnung enthält mehrere Steuersätze. Bitte die Aufteilung in Netto und Umsatzsteuer angeben.',
                [
                    {
                        code: 'allocation_required',
                        level: 'error',
                        field: 'allocation',
                        message:
                            'Bei mehreren Steuersätzen muss die Aufteilung des Zahlbetrags angegeben werden.',
                    },
                ],
            );
        }
        allocation = auto;
    }

    if (allocation.allocatedNetCents + allocation.allocatedTaxCents !== input.amountCents) {
        throw new PaymentValidationError(
            `Die Aufteilung ergibt ${formatCents(
                allocation.allocatedNetCents + allocation.allocatedTaxCents,
            )} statt ${formatCents(input.amountCents)}.`,
            [
                {
                    code: 'allocation_mismatch',
                    level: 'error',
                    field: 'allocation',
                    message: 'Netto und Umsatzsteuer müssen zusammen den Zahlbetrag ergeben.',
                },
            ],
        );
    }

    return {
        invoice_id: input.invoiceId,
        direction: input.direction,
        value_date: input.valueDate,
        amount_cents: input.amountCents,
        allocated_net_cents: allocation.allocatedNetCents,
        allocated_tax_cents: allocation.allocatedTaxCents,
        method: input.method,
        reference: input.reference ?? null,
        bank_tx_id: input.bankTxId ?? null,
        note: input.note ?? null,
    };
};

/**
 * Fehler der Datenbank beim Schreiben einer Zahlung in Klartext.
 *
 * 23514 auf der Zahlungsart heisst: die Datenbank kennt die Zahlungsart noch
 * nicht — Skonto braucht SQL Nr. 121, der Sicherheitseinbehalt Nr. 125/126.
 * Ohne diese Uebersetzung saehe der Nutzer nur "violates check constraint".
 */
const paymentWriteError = (error: unknown): unknown => {
    const e = error as { code?: string; message?: string; hint?: string };
    if (e?.code === '23514' && (e.message ?? '').includes('invoice_payments_method_check')) {
        return new Error(
            'Die Datenbank kennt diese Zahlungsart noch nicht (Skonto: SQL Nr. 121, Sicherheitseinbehalt: Nr. 125 und 126). Es wurde nichts gebucht.',
        );
    }
    // Riegel Nr. 122/125 (Skonto oder Einbehalt ueber dem offenen Rest) und
    // der Alt-Einbehalt-Trigger Nr. 125 (Rechnung ohne Projekt): die Meldung
    // der Datenbank ist bereits Klartext; als Error weitergereicht, damit die
    // Masken sie zeigen.
    const hint = e?.hint ?? '';
    if (
        (hint.includes('invoice_payments_cash_discount_guard') ||
            hint.includes('invoice_payments_retention_backfill_sync') ||
            hint.includes('invoice_payments_bank_tx_guard')) &&
        e.message
    ) {
        return new Error(e.message);
    }
    // 40P01 = deadlock_detected, 40001 = serialization_failure: zwei Buchungen
    // trafen im selben Moment auf dieselbe Rechnung (Fremdschluessel-Sperre und
    // `for update` in den Triggern). Die Datenbank hat DIESE Buchung
    // vollstaendig zurueckgenommen — ein zweiter Versuch ist sicher. Fruehere
    // Buchungen desselben Importlaufs bleiben bestehen.
    if (e?.code === '40P01' || e?.code === '40001') {
        return new Error(
            'Diese Buchung wurde nicht gespeichert — vermutlich wurde im selben Moment auf dieselbe Rechnung gebucht. Bitte neu laden und erneut versuchen.',
        );
    }
    return error;
};

/**
 * Bucht eine Zahlung auf eine Rechnung.
 *
 * Die Netto/Steuer-Aufteilung ist Pflicht — Umsatzsteuervoranmeldung und
 * DATEV-Export brauchen sie. Bei einem einzigen Steuersatz wird sie gerechnet;
 * bei mehreren liefert allocatePayment bewusst null, und der Aufrufer muss eine
 * fachlich bestaetigte Aufteilung mitgeben, statt dass das System schaetzt.
 */
export const addPayment = async (
    input: AddPaymentInput,
    invoice: Invoice,
    todayIso: string,
): Promise<InvoicePayment> => {
    const row = preparePaymentRow(input, invoice, todayIso);

    const { data, error } = await supabase
        .from('invoice_payments')
        .insert(row)
        .select('*')
        .single();

    // 23505 = unique_violation. Bei gesetzter bank_tx_id heisst das: derselbe
    // Kontoauszugsposten wurde bereits gebucht. Das ist kein Fehler, sondern
    // genau der Zweck des Schluessels — die vorhandene Zahlung wird
    // zurueckgegeben, statt eine zweite anzulegen.
    if (error) {
        if ((error as { code?: string }).code === '23505' && input.bankTxId) {
            const existing = await findPaymentByBankTxId(input.bankTxId);
            // Die Eindeutigkeit von bank_tx_id gilt ueber ALLE Rechnungen. Eine
            // Kollision heisst deshalb zweierlei: entweder derselbe Umsatz auf
            // derselben Rechnung (harmlose Wiederholung) oder derselbe Umsatz
            // auf einer ANDEREN Rechnung (eine Fehlzuordnung, die niemand
            // bemerkt, wenn man hier die fremde Zahlung zurueckgibt).
            if (existing?.invoiceId === input.invoiceId) return existing;
            if (existing) {
                throw new Error(
                    `Dieser Kontoauszugsposten ist bereits einer anderen Rechnung zugeordnet (Zahlung vom ${existing.valueDate}). Bitte dort prüfen, statt ihn hier erneut zu buchen.`,
                );
            }
        }
        throw paymentWriteError(error);
    }

    return mapPaymentRow(data);
};

export interface CashDiscountInput {
    /** Skonto in Cent — in der Regel offener Betrag minus Zahlung. */
    amountCents: number;
    /** Nur bei mehreren Steuersaetzen noetig, wie bei der Zahlung selbst. */
    allocation?: { allocatedNetCents: number; allocatedTaxCents: number };
}

export interface RemainderInput extends CashDiscountInput {
    /** Skonto oder vom Kunden einbehaltener Sicherheitseinbehalt. */
    kind: RemainderKind;
}

/** Zeile fuer den Rest einer Zahlung (Skonto oder Einbehalt) — ohne zu schreiben. */
const prepareRemainderRow = (
    payment: AddPaymentInput,
    remainder: RemainderInput,
    invoice: Invoice,
    todayIso: string,
) =>
    preparePaymentRow(
        {
            invoiceId: payment.invoiceId,
            direction: 'INCOMING',
            valueDate: payment.valueDate,
            amountCents: remainder.amountCents,
            method: remainder.kind,
            reference: `${REMAINDER_LABEL[remainder.kind]} zur Zahlung über ${formatCents(payment.amountCents)}`,
            note: payment.note,
            allocation: remainder.allocation,
        },
        invoice,
        todayIso,
        payment.amountCents,
    );

/**
 * Schreibt vorbereitete Zeilen in EINER Anweisung — ein insert mit mehreren
 * Zeilen ist eine Transaktion: alle oder keine.
 *
 * 23505 auf bank_tx_id heisst: dieser Kontoauszugsposten (oder ein Teil davon)
 * ist schon gebucht. Dann wird NICHTS gebucht — ob dort bereits Skonto oder
 * Einbehalt steht, muss ein Mensch an der Rechnung ansehen.
 */
const insertRowsAtomically = async (
    rows: Array<Record<string, unknown>>,
    bankTxId: string | undefined,
): Promise<InvoicePayment[]> => {
    const { data, error } = await supabase.from('invoice_payments').insert(rows).select('*');
    if (error) {
        if ((error as { code?: string }).code === '23505' && bankTxId) {
            throw new Error(
                'Dieser Kontoauszugsposten ist bereits gebucht. Es wurde nichts gebucht — bitte an der Rechnung prüfen, was dort schon steht.',
            );
        }
        throw paymentWriteError(error);
    }
    const booked = (data ?? []).map(mapPaymentRow);
    if (booked.length !== rows.length) {
        throw new Error(
            'Die Datenbank hat die Buchung bestätigt, aber nicht alle Zeilen zurückgegeben. Bitte die Rechnung neu laden und prüfen — NICHT erneut buchen.',
        );
    }
    return booked;
};

/**
 * Bucht eine Zahlung und den Rest (Skonto oder Sicherheitseinbehalt) in
 * EINEM Schritt.
 *
 * Zwei getrennte Aufrufe liessen bei einem Fehler dazwischen die Zahlung ohne
 * Rest stehen: die Rechnung bliebe mit dem Betrag offen und liefe in die
 * Mahnung, ohne dass es jemandem auffaellt. Fehlt die Migration fuer die
 * Zahlungsart, scheitert so der ganze Aufruf und es steht gar nichts in der
 * Datenbank. Beim Einbehalt traegt der Trigger aus Nr. 125 in derselben
 * Transaktion den Alt-Einbehalt des Projekts ein.
 */
export const addPaymentWithRemainder = async (
    payment: AddPaymentInput,
    remainder: RemainderInput,
    invoice: Invoice,
    todayIso: string,
): Promise<{ payment: InvoicePayment; remainder: InvoicePayment }> => {
    if (isRemainderMethod(payment.method) || payment.direction !== 'INCOMING') {
        throw new PaymentValidationError(
            `${REMAINDER_LABEL[remainder.kind]} lässt sich nur zu einem Zahlungseingang buchen.`,
            [],
        );
    }
    const rows = [
        preparePaymentRow(payment, invoice, todayIso),
        prepareRemainderRow(payment, remainder, invoice, todayIso),
    ];
    const booked = await insertRowsAtomically(rows, payment.bankTxId);
    const bookedPayment = booked.find((row) => !isRemainderMethod(row.method));
    const bookedRemainder = booked.find((row) => row.method === remainder.kind);
    if (!bookedPayment || !bookedRemainder) {
        throw new Error(
            'Die Datenbank hat die Buchung bestätigt, aber nicht beide Zeilen zurückgegeben. Bitte die Rechnung neu laden und prüfen — NICHT erneut buchen.',
        );
    }
    return { payment: bookedPayment, remainder: bookedRemainder };
};

/**
 * Zahlung plus Skonto — Kurzform von addPaymentWithRemainder.
 */
export const addPaymentWithCashDiscount = async (
    payment: AddPaymentInput,
    discount: CashDiscountInput,
    invoice: Invoice,
    todayIso: string,
): Promise<{ payment: InvoicePayment; discount: InvoicePayment }> => {
    const { payment: booked, remainder } = await addPaymentWithRemainder(
        payment,
        { ...discount, kind: 'CASH_DISCOUNT' },
        invoice,
        todayIso,
    );
    return { payment: booked, discount: remainder };
};

// ─── Aufgeteilte Zahlung: ein Umsatz, mehrere Rechnungen ────────────────────

export interface SplitPart {
    invoice: Invoice;
    /** Auf diese Rechnung entfallender Teil des Umsatzes. */
    amountCents: number;
    remainder?: RemainderInput;
    allocation?: { allocatedNetCents: number; allocatedTaxCents: number };
}

/**
 * Verteilt EINEN Zahlungseingang auf mehrere Rechnungen — Sammelzahlung.
 *
 * Alle Teile samt Skonto/Einbehalt gehen in einer Anweisung an die
 * Datenbank. Die Teile muessen zusammen genau den Umsatz ergeben; dieselbe
 * Rechnung darf nur einmal vorkommen.
 */
export const addSplitPayment = async (
    base: Omit<AddPaymentInput, 'invoiceId' | 'amountCents' | 'allocation'>,
    totalCents: number,
    parts: readonly SplitPart[],
    todayIso: string,
): Promise<InvoicePayment[]> => {
    if (base.direction !== 'INCOMING' || isRemainderMethod(base.method)) {
        throw new PaymentValidationError('Aufteilen lässt sich nur ein Zahlungseingang.', []);
    }
    if (parts.length < 2) {
        throw new PaymentValidationError('Zum Aufteilen braucht es mindestens zwei Rechnungen.', []);
    }
    const ids = new Set(parts.map((part) => part.invoice.id));
    if (ids.size !== parts.length) {
        throw new PaymentValidationError('Dieselbe Rechnung steht mehrfach in der Aufteilung.', []);
    }
    const sum = parts.reduce((total, part) => total + part.amountCents, 0);
    if (sum !== totalCents) {
        throw new PaymentValidationError(
            `Die Teile ergeben ${formatCents(sum)}, der Umsatz beträgt ${formatCents(totalCents)}.`,
            [],
        );
    }

    const rows: Array<Record<string, unknown>> = [];
    parts.forEach((part, index) => {
        const payment: AddPaymentInput = {
            ...base,
            invoiceId: part.invoice.id,
            amountCents: part.amountCents,
            allocation: part.allocation,
            bankTxId: base.bankTxId ? splitBankTxId(base.bankTxId, index) : undefined,
        };
        rows.push(preparePaymentRow(payment, part.invoice, todayIso));
        if (part.remainder && part.remainder.amountCents > 0) {
            rows.push(prepareRemainderRow(payment, part.remainder, part.invoice, todayIso));
        }
    });
    return insertRowsAtomically(rows, base.bankTxId);
};

/**
 * Alle Zahlungen zu einem Umsatz — der Umsatz selbst und die Teile einer
 * Aufteilung (Schluessel `<umsatz>#T<n>`).
 */
export const findPaymentsForBankTx = async (bankTxId: string): Promise<InvoicePayment[]> => {
    const base = baseBankTxId(bankTxId);
    const escaped = base.replace(/[\\%_]/g, (ch) => `\\${ch}`);
    const [exact, parts] = await Promise.all([
        supabase.from('invoice_payments').select('*').eq('bank_tx_id', base),
        supabase.from('invoice_payments').select('*').like('bank_tx_id', `${escaped}#T%`),
    ]);
    if (exact.error) throw exact.error;
    if (parts.error) throw parts.error;
    // `#T%` liesse auch andere Endungen durch — nur echte Teilnummern zaehlen.
    const isPart = (id: unknown) =>
        typeof id === 'string' && id.startsWith(`${base}#T`) && /^\d+$/.test(id.slice(base.length + 2));
    return [...(exact.data ?? []), ...(parts.data ?? []).filter((row) => isPart(row.bank_tx_id))].map(
        mapPaymentRow,
    );
};

export const findPaymentByBankTxId = async (bankTxId: string): Promise<InvoicePayment | null> => {
    const { data, error } = await supabase
        .from('invoice_payments')
        .select('*')
        .eq('bank_tx_id', bankTxId)
        .maybeSingle();
    if (error) throw error;
    return data ? mapPaymentRow(data) : null;
};

/**
 * Loescht eine Zahlung.
 *
 * Nicht moeglich, sobald aus ihr ein Kundenguthaben entstanden ist: das
 * Guthaben kann bereits verrechnet oder erstattet sein, und ein Loeschen wuerde
 * einen Betrag verschwinden lassen, der an anderer Stelle schon gewirkt hat.
 * Dann ist die Erstattung (direction OUTGOING) der richtige Weg.
 */
export const deletePayment = async (paymentId: string): Promise<void> => {
    const { data: credits, error: creditErr } = await supabase
        .from('customer_credits')
        .select('id, used_cents')
        .eq('source_payment_id', paymentId);
    if (creditErr) throw creditErr;
    if (credits?.length) {
        throw new Error(
            'Aus dieser Zahlung ist bereits ein Kundenguthaben entstanden. Sie kann nicht gelöscht werden — bitte stattdessen eine Erstattung erfassen.',
        );
    }

    const { error } = await supabase.from('invoice_payments').delete().eq('id', paymentId);
    if (error) throw error;
};

// ─── Kundenguthaben ─────────────────────────────────────────────────────────

/**
 * Wandelt die Ueberzahlung einer Rechnung in ein Kundenguthaben.
 *
 * Bezugspunkt ist die RECHNUNG, nicht eine einzelne Zahlung: der Ueberhang
 * entsteht aus allen Zahlungen und Verrechnungen zusammen, und ihn einer
 * beliebigen Zahlung zuzuschreiben ergaebe ein Guthaben, dessen Deckung an
 * einer Zeile haengt, die mit dem Betrag nichts zu tun hat.
 *
 * Die RPC schreibt nur den Teil gut, fuer den es noch kein Guthaben gibt — ein
 * zweiter Aufruf findet nichts mehr vor und bricht ab.
 */
export const createOverpaymentCredit = async (
    invoiceId: string,
    note?: string,
): Promise<string> => {
    const { data, error } = await supabase.rpc('create_overpayment_credit', {
        p_invoice_id: invoiceId,
        p_note: note ?? null,
    });
    if (error) throw error;
    return data as string;
};

/**
 * Verrechnet ein Guthaben mit einer offenen Rechnung.
 *
 * Die Ueberverwendung wird NICHT hier geprueft, sondern von der Datenbank:
 * customer_credits traegt CHECK (used_cents between 0 and amount_cents), und
 * der Trigger rechnet used_cents aus den Verrechnungszeilen neu. Ein zu grosser
 * Betrag laesst die gesamte Transaktion scheitern — auch dann, wenn zwei
 * Verrechnungen gleichzeitig gebucht werden. Eine Pruefung im Browser koennte
 * das nicht leisten.
 */
export const allocateCredit = async (
    creditId: string,
    targetInvoiceId: string,
    amountCents: number,
): Promise<CreditAllocation> => {
    if (!Number.isSafeInteger(amountCents) || amountCents <= 0) {
        throw new Error('Der Verrechnungsbetrag muss größer als 0,00 € sein.');
    }
    const { data, error } = await supabase
        .from('credit_allocations')
        .insert({
            credit_id: creditId,
            target_invoice_id: targetInvoiceId,
            allocated_cents: amountCents,
        })
        .select('*')
        .single();
    if (error) {
        if ((error as { code?: string }).code === '23514') {
            throw new Error(
                'Das Guthaben reicht für diesen Betrag nicht aus. Bitte den verfügbaren Rest prüfen.',
            );
        }
        throw error;
    }
    return mapAllocationRow(data);
};

/**
 * Groesstmoeglicher sinnvoller Verrechnungsbetrag: nie mehr als das Guthaben
 * hergibt und nie mehr, als die Zielrechnung offen hat.
 */
export const maxAllocatableCents = (credit: CustomerCredit, target: Invoice): number =>
    Math.max(0, Math.min(credit.amountCents - credit.usedCents, openAmountCents(target)));

// ─── Mahnwesen ──────────────────────────────────────────────────────────────

/**
 * Vermerkt, dass eine Mahnung hinausgegangen ist.
 *
 * Bewusst getrennt vom Erzeugen des Mahnschreibens: gespeichert wird das
 * Ereignis, nicht die Absicht. Wer den Versand abbricht, hat nicht gemahnt.
 */
export const recordDunning = async (invoiceId: string, level: number): Promise<void> => {
    if (!Number.isInteger(level) || level < 1 || level > 3) {
        throw new Error('Ungültige Mahnstufe.');
    }
    const { data, error } = await supabase
        .from('invoices')
        .update({ dunning_level: level, dunning_last_at: new Date().toISOString() })
        .eq('id', invoiceId)
        // Nie zurueckstufen: eine bereits versandte Mahnung laesst sich nicht
        // ungeschehen machen.
        .lt('dunning_level', level)
        .select('id');
    if (error) throw error;
    // Null Zeilen heisst: die Bedingung hat nicht gepasst, die Mahnstufe steht
    // bereits auf diesem Wert oder hoeher. PostgREST meldet das als Erfolg —
    // ohne diese Pruefung wuerde die Oberflaeche "Mahnstufe vermerkt" melden,
    // obwohl nichts geschrieben wurde.
    if (!data || data.length === 0) {
        throw new Error(
            'Die Mahnstufe wurde bereits erhöht — vermutlich von einer anderen Stelle. Bitte die Rechnung neu laden.',
        );
    }
};

/** Ueberzahlung einer Rechnung — Kurzform fuer die Oberflaeche. */
export const invoiceOverpaymentCents = (invoice: Invoice): number =>
    overpaymentCents(invoice.totals.payableCents, invoice.paidCents, invoice.creditedCents);
